import classNames from "classnames";
import * as React from "react";
import { dateUtility } from "~/EasyProjectsCommon/Dates/DateUtility";
import { commonClientResx } from "~/resxDist/commonClientResx";
import { Icon, icon } from "~/sharedComponents/icons";
import { epFormat } from "~/utils/epFormat";
import { Indent } from "../indent";
import * as styles from "./Qtip.less";

export const More: React.FC<{ count?: number }> = ({ count }) => (
    <span>{!!count && count > 0 ? epFormat(commonClientResx.more_counter_text, count) : "..."}</span>
);

export const Title: React.FC = ({ children }) => (
    <div data-testid="title" className={classNames(styles.title, styles.indent, styles.inner)}>
        {children}
    </div>
);

type DatesProps = {
    StartDate?: Date;
    EndDate?: Date;
};

export const Dates: React.FC<DatesProps> = ({ StartDate, EndDate, children }) => {
    let dateRange = dateUtility.formatDatesRange(StartDate, EndDate);
    if (StartDate === EndDate && StartDate && EndDate) {
        dateRange += ", " + dateUtility.formatAsDayName(StartDate);
    }

    return (
        <Title>
            <Icon iconName={icon.calendar} />
            {dateRange}
            {children}
        </Title>
    );
};

/**
 * Renders the name and value of the metric.
 * Expects an icon as an optional child.
 */
export const Metric: React.FC<{ name?: string; value: string }> = ({ name, value, children: icon }) => (
    <div className={classNames(styles.metric_with_icon, styles.indent)}>
        <IconWithIndent>{icon}</IconWithIndent>
        <div>
            <div className={styles.title}>{name}</div>
            {value}
        </div>
    </div>
);

export enum ValueTheme {
    danger = "danger",
    warning = "warning",
    success = "success",
}

export const Value: React.FC<{ name?: string; value: string; theme?: ValueTheme }> = ({
    name,
    value,
    theme,
    children: icon,
}) => (
    <div className={classNames(styles.value_wrapper, styles.indent, theme && styles[theme])}>
        <div className={styles.label}>
            {icon && <div className={styles.icon}>{icon}</div>}
            {name}
        </div>
        <div className={styles.value}>{value}</div>
    </div>
);

/**
 * Aligns an element less than the default line-height (20px) vertically and horizontally relative to the subsequent text.
 */
const IconWithIndent: React.FC = ({ children }) =>
    notEmpty(children) && (
        <div className={styles.text_align}>
            {children}
            <Indent />
        </div>
    );

export const Line: React.FC = () => <hr className={styles.line} />;

const notEmpty = (children?: React.ReactNode) => React.Children.toArray(children).some(child => !!child);
